import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { WorkOrderDetailsDto, UserDto, FeatureSettingsDto, CommentDto } from '@app/generated/model';
import { UserService } from '@app/core/services/user.service';
import { ProjectControllerService } from '@app/generated/api/projectController.service';
import { SearchIndex } from 'src/app/utils';

import { WorkOrderEquipmentService } from './work-order-equipment.service';
import { Observable, Subject, Subscriber, Subscription } from 'rxjs';
import { WorkOrderEquipment } from './work-order-equipment.model';
import { map, takeUntil } from 'rxjs/operators';
import { WebSocketService } from '@app/core/services/websocket.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import { BadgeNotification } from '@app/core/model/badge-notification.model';
import { Store } from '@ngrx/store';
import { State } from '@dwo/reducers';
import { getFeatures } from '@app/core/store/selectors/settings.selector';
import {
  LoadMIPSCommentsAction,
  LoadDocuments,
  LoadSettings,
  LoadWorkOrder,
  LoadWorkOrderMaterials,
  LoadWorkOrderObjects,
} from '@dwo/actions';
import { getMIPSComments } from '@dwo/selectors';
import { commentsReducer } from '@app/core/store/reducers/comments';
import { ActivatedRoute, Router } from '@angular/router';
import { getCurrentWorkOrder } from '@app/core/store/selectors/work-order.selector';

const MAX_NUMBER_OF_OPERATORS = 10;

@Component({
  selector: 'app-work-order-overview',
  templateUrl: './work-order-overview.component.html',
  styleUrls: ['./work-order-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkOrderOverviewComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly authorizedProjectsById = new Map<string, string>();
  private loadedProjectsForUsername = '';

  releasedToAll = false;
  workOrder: WorkOrderDetailsDto;
  user: UserDto;
  equipment: WorkOrderEquipment[];
  mipsComments: CommentDto[];
  workOrderProjectName = '';
  searchText = '';

  private notificationSubscription$: Subscription;
  constructor(
    private userService: UserService,
    private projectControllerService: ProjectControllerService,
    private workOrderEquipmentService: WorkOrderEquipmentService,
    private webSocketService: WebSocketService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private store: Store<State>,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.store
      .select(getMIPSComments)
      .pipe(takeUntil(this.destroy$))
      .subscribe((comments) => {
        if (!!comments && comments.length > 0) {
          this.mipsComments = comments;
        }
      });
    this.store.dispatch(new LoadSettings());
  }

  ngOnInit() {
    const workOrderId = this.route.snapshot.paramMap.get('id');
    this.store.dispatch(new LoadWorkOrder(workOrderId));

    this.store
      .select(getCurrentWorkOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((workOrder) => {
        if (!!workOrder) {
          this.workOrder = workOrder;
          this.updateWorkOrderProjectName();
          this.store.dispatch(new LoadMIPSCommentsAction(workOrder.id));
          this.initSocket();
          this.store.dispatch(new LoadWorkOrderObjects(workOrder.id));
          this.store.dispatch(new LoadMIPSCommentsAction(workOrder.id));
          this.store.dispatch(new LoadDocuments(workOrder.id));
          this.store.dispatch(new LoadWorkOrderMaterials(workOrder.id));
        }
      });

    this.workOrderEquipmentService.data$.pipe(takeUntil(this.destroy$)).subscribe((equipment) => {
      if (!!equipment) {
        this.equipment = equipment;
      }
    });

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((authenticatedUser) => {
        this.user = authenticatedUser;

        const username = (authenticatedUser?.username || '').toString().trim();
        if (!username) {
          this.authorizedProjectsById.clear();
          this.updateWorkOrderProjectName();
          return;
        }

        this.loadAuthorizedProjectsForUser(username);
      });
  }

  private loadAuthorizedProjectsForUser(username: string): void {
    if (this.loadedProjectsForUsername === username && this.authorizedProjectsById.size > 0) {
      this.updateWorkOrderProjectName();
      return;
    }

    this.loadedProjectsForUsername = username;
    this.authorizedProjectsById.clear();

    this.projectControllerService
      .getAuthorizedProjects(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe((projects) => {
        (projects || []).forEach((project) => {
          const projectId = (project?.id || '').toString().trim();
          const projectName = (project?.name || '').toString().trim();
          if (projectId && projectName) {
            this.authorizedProjectsById.set(projectId, projectName);
          }
        });

        this.updateWorkOrderProjectName();
      });
  }

  initSocket(): void {
    this.notificationSubscription$ = this.webSocketService.badgeNotifications$.subscribe(
      async (notifications: BadgeNotification[]) => {
        if (notifications == null) {
          return;
        }

        const workOrderNotification = notifications.filter(
          (notification: BadgeNotification) =>
            notification.name.endsWith('WorkOrderEntity') && notification.id === this.workOrder.id,
        )[0];

        // if (
        //   workOrderNotification &&
        //   !workOrderNotification.refreshTriggered &&
        //   (await this.workOrderService.isDownloaded())
        // ) {
        //   this.refreshOfflineData(workOrderNotification);
        // }
        if (workOrderNotification && workOrderNotification.children.length <= 0) {
          this.webSocketService.sendNotificationsViewed([workOrderNotification]);
        }
      },
    );
  }

  ngOnDestroy() {
    if (this.notificationSubscription$) {
      this.notificationSubscription$.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearSearchText() {
    this.searchText = '';
  }

  navigateToWorkOrder(workOrderId: string | undefined | null): void {
    if (!workOrderId || workOrderId === this.workOrder?.id) {
      return;
    }

    // Navigating between two 'work-order/:id' routes reuses the same component instance, so we need to force a reload of the component by navigating to a dummy route first.
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/work-order', workOrderId]);
    });
  }

  private updateWorkOrderProjectName(): void {
    const projectId = this.workOrder?.projectId;
    if (!projectId) {
      this.workOrderProjectName = '';
      return;
    }

    this.workOrderProjectName = (this.authorizedProjectsById.get(projectId) || '').toString().trim();
  }

  shouldShowWorkOrderProjectName(): boolean {
    if (!this.workOrderProjectName) {
      return false;
    }

    const selectedProjects = this.user?.projects || [];
    if (selectedProjects.length > 1) {
      return true;
    }

    if (selectedProjects.length === 1) {
      const selectedProjectId = (selectedProjects[0]?.id || '').toString().trim();
      const workOrderProjectId = (this.workOrder?.projectId || '').toString().trim();
      return !!selectedProjectId && !!workOrderProjectId && selectedProjectId !== workOrderProjectId;
    }

    return false;
  }

  formatLabel(value: number) {
    return `${value} %`;
  }

  async claim() {
    // await this.workOrderService.claim();
    //
    // const workOrder = this.workOrderService.get();
    // await this.workOrderTimerService.initializeTimerForWorkOrder(workOrder);
  }

  releaseToAll() {
    // this.workOrderService.releaseToAll();
  }

  revertReleaseToAll() {
    // this.workOrderService.revertReleaseToAll();
  }

  toggleFavourite() {
    // this.workOrderService.toggleFavouriteWorkOrder();
  }

  private async refreshOfflineData(notification: BadgeNotification) {
    if (navigator.onLine) {
      try {
        // this.workOrderService.refreshOfflineData(notification);
        this.webSocketService.sendRefreshTriggered(this.workOrder.id);
      } catch (e) {
        console.error(e);
      }
    }
  }
}
